/**
 * /admin/v1/intent/* API 客户端 · M02 意图管理（S2 Day 6b）
 *
 * 承接：
 * - 后端 schema：apps/api/src/csa_api/admin/intent_admin.py（Day 6a 落地 · 5 endpoint）
 * - T11 §3.1 admin/v1/* 路由约定
 *
 * 类型来源：S2 PR-X 接 packages/api-types（openapi-typescript 生成）；本地保留最小手写集 · 与后端 P11 P0 13 intent + L2 快路径摘要由集成测试 + OpenAPI drift CI 兜底。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

// ─── DTO（与后端 Pydantic 同源 · 改动需双向同步 · S2 PR-X openapi-typescript 自动化） ─

export interface IntentL2RuleSummary {
	rules_count: number;
	enabled_count: number;
	sample_keywords_zh: string[];
	sample_keywords_en: string[];
}

export interface IntentListItem {
	intent: string;
	description_en: string;
	description_zh: string;
	service_stage: "pre_sale" | "in_sale" | "after_sale" | "unknown";
	l2_summary: IntentL2RuleSummary;
}

export interface IntentListResponse {
	items: IntentListItem[];
	bundle_version: string;
	bundle_author: string;
	schema_version: string;
	total_rules: number;
}

export interface L2RulesGetResponse {
	yaml_text: string;
	bundle_version: string;
	bundle_author: string;
	schema_version: string;
	total_rules: number;
	enabled_rules: number;
}

export interface L2RulesPutRequest {
	yaml_text: string;
	expected_version?: string | null;
}

export interface L2RulesPutResponse {
	bundle_version: string;
	total_rules: number;
	cache_invalidated: boolean;
}

export interface L1PromptTemplate {
	version_id: string;
	title: string;
	system_prompt: string;
	few_shot_examples: string;
	notes: string | null;
	updated_by: string;
	updated_at: string;
}

export interface L1PromptTemplatesGetResponse {
	yaml_text: string;
	active_version: string;
	templates_total: number;
	current_template: L1PromptTemplate;
}

export interface L1PromptTemplatesPutRequest {
	yaml_text: string;
	expected_active_version?: string | null;
}

export interface L1PromptTemplatesPutResponse {
	active_version: string;
	templates_total: number;
	cache_invalidated: boolean;
}

export interface L1PromptABConfigResponse {
	merchant_id: string;
	l1_prompt_version_a: string;
	l1_prompt_version_b: string | null;
	l1_ab_ratio: number;
	updated_at: string;
}

export interface L1PromptABConfigPutRequest {
	l1_prompt_version_a: string;
	l1_prompt_version_b: string | null;
	l1_ab_ratio: number;
}

// ─── Hooks ────────────────────────────────────────────────────────────────

export function useIntentList() {
	return useQuery<IntentListResponse>({
		queryKey: ["admin", "intent", "list"],
		queryFn: async () => {
			const { data } = await apiClient.get<IntentListResponse>("/admin/v1/intent/list");
			return data;
		},
	});
}

export function useL2Rules() {
	return useQuery<L2RulesGetResponse>({
		queryKey: ["admin", "intent", "l2_rules"],
		queryFn: async () => {
			const { data } = await apiClient.get<L2RulesGetResponse>("/admin/v1/intent/l2_rules");
			return data;
		},
	});
}

export function usePutL2Rules() {
	const queryClient = useQueryClient();
	return useMutation<L2RulesPutResponse, unknown, L2RulesPutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<L2RulesPutResponse>("/admin/v1/intent/l2_rules", body);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "intent", "list"] });
			queryClient.invalidateQueries({ queryKey: ["admin", "intent", "l2_rules"] });
		},
	});
}

export function useL1PromptTemplates() {
	return useQuery<L1PromptTemplatesGetResponse>({
		queryKey: ["admin", "intent", "l1_prompt_templates"],
		queryFn: async () => {
			const { data } = await apiClient.get<L1PromptTemplatesGetResponse>(
				"/admin/v1/intent/l1_prompt_templates",
			);
			return data;
		},
	});
}

export function usePutL1PromptTemplates() {
	const queryClient = useQueryClient();
	return useMutation<L1PromptTemplatesPutResponse, unknown, L1PromptTemplatesPutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<L1PromptTemplatesPutResponse>(
				"/admin/v1/intent/l1_prompt_templates",
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "intent", "l1_prompt_templates"] });
		},
	});
}

export function useL1PromptABConfig(merchantId: string | null) {
	return useQuery<L1PromptABConfigResponse>({
		queryKey: ["admin", "intent", "l1_prompt", merchantId],
		queryFn: async () => {
			if (!merchantId) throw new Error("merchant_id is required");
			const { data } = await apiClient.get<L1PromptABConfigResponse>(
				`/admin/v1/intent/merchants/${encodeURIComponent(merchantId)}/l1_prompt`,
			);
			return data;
		},
		enabled: Boolean(merchantId),
	});
}

export function usePutL1PromptABConfig(merchantId: string) {
	const queryClient = useQueryClient();
	return useMutation<L1PromptABConfigResponse, unknown, L1PromptABConfigPutRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.put<L1PromptABConfigResponse>(
				`/admin/v1/intent/merchants/${encodeURIComponent(merchantId)}/l1_prompt`,
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "intent", "l1_prompt", merchantId] });
		},
	});
}
